import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import api from 'api';
import { getEmployeeCustomFields } from 'api/gql-utils/employeeCustomFields/queries/getEmployeeCustomFields';
import { DeductFrom } from 'components/RunPayroll/utils/types';
import { useHrisCustomFieldsFlag } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/hooks/useHrisCustomFieldsFlag';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { GetFnfResponseSchemaContract } from 'schemas/GetFnfResponseSchema';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { dateFormats } from 'utils/Dates';
import { generateUserId } from 'utils/commons';
import { showErrorToastNotification, showToastViaEvent } from 'utils/ToastEvents';
import { GET_FNF_DATA, GET_FNF_DISMISSAL_DATE, GET_FNF_HRIS_FIELDS, UPDATE_FNF_DATA } from '../queries';
import { ExistingSalaryComponents } from '../types';
import useIsHoldSalaryPayComplianceEnabled from '../useIsHoldSalaryPayComplianceEnabled';
import {
  makeAdditionOfMinimumLenth,
  makeNonLopDeductionsOfMinimumLength,
  prepareFnfDataForUpdate,
} from '../utils';

const useFnfApi = ({
  dismissalDateInput,
  setDismissalDateInput,
  selectedHoldOption,
  setSelectedHoldOption,
  peopleId,
}: {
  dismissalDateInput: Date | null;
  setDismissalDateInput: React.Dispatch<React.SetStateAction<Date | null>>;
  setSelectedHoldOption: React.Dispatch<React.SetStateAction<string>>;
  selectedHoldOption: string;
  peopleId: string | null;
}) => {
  const [fnfData, setFnfData] = useState<null | GetFnfResponseSchemaContract>(null);
  const [existingComponents, setExistingComponents] = useState<ExistingSalaryComponents>({
    additions: [],
    deductions: [],
  });
  const { isM2S1Enabled, isM2S2Enabled, isM3S1Disabled } = useIsFeatureDisabledForRearch();
  const { isHoldSalaryPayComplianceEnabled } = useIsHoldSalaryPayComplianceEnabled();
  const [originalDismissalDate, setOriginalDismissalDate] = useState<string | null>(null);
  const isHrisCustomFieldsEnabled = useHrisCustomFieldsFlag();

  const isFnfProcessed = fnfData?.is_fnf_processed ?? false;

  // hrisCustomFields orgs: the personal email is HRIS-sourced — read it
  // directly and use it as the prefill.
  const hrisFieldsQuery = useQuery({
    enabled: !!peopleId && isHrisCustomFieldsEnabled,
    queryKey: [GET_FNF_HRIS_FIELDS, peopleId],
    // the BFF requires the user_<id> format
    queryFn: () => getEmployeeCustomFields({ userId: generateUserId(String(peopleId)) }),
    onSuccess: (data) => {
      const hrisEmail = data.payrollEmployeeCustomFields?.fields?.['personal_email_address'];
      if (typeof hrisEmail === 'string' && hrisEmail !== '') {
        setFnfData((prev) => (prev ? { ...prev, personal_email_address: hrisEmail } : prev));
      }
    },
  });

  const dismissalDateQuery = useQuery({
    queryKey: [GET_FNF_DISMISSAL_DATE, peopleId],
    enabled: !!peopleId,
    queryFn: () => {
      if (!peopleId) {
        return Promise.reject('Give people id');
      }
      if (isM2S1Enabled) {
        return api.salaryComponents.queries.getDismissalDate({ userId: peopleId });
      } else {
        return api.fnf.getDismissalDate(parseInt(peopleId));
      }
    },
    onSuccess: (data) => {
      const originalDismissalDateValue = data.deactivate_date || data.resignation_date;
      let dismissalDateForInput = new Date();
      if (data.deactivate_date) {
        dismissalDateForInput = new Date(data.deactivate_date);
      } else if (data.resignation_date) {
        dismissalDateForInput = new Date(data.resignation_date);
      }
      setDismissalDateInput(dismissalDateForInput);
      setOriginalDismissalDate(originalDismissalDateValue);
    },
  });

  const fnfDataQuery = useQuery({
    enabled: !!peopleId && !!dismissalDateInput,
    queryKey: [GET_FNF_DATA, peopleId, dismissalDateInput],
    queryFn: () => {
      if (peopleId && dismissalDateInput) {
        const dismissalDateParam = format(new Date(dismissalDateInput), dateFormats.yearMonthDate);
        if (isM2S2Enabled) {
          return api.salaryComponents.queries.getFnfDetailsV2({
            userId: peopleId,
            deactivateDate: dismissalDateParam,
            payType: 'LIST_ADHOC_TYPE',
          });
        } else if (isM2S1Enabled) {
          return api.salaryComponents.queries.getFnfDetails({
            userId: peopleId,
            deactivateDate: dismissalDateParam,
            payType: 'LIST_ADHOC_TYPE',
          });
        } else {
          return api.fnf.getFnfData(parseInt(peopleId), dismissalDateParam);
        }
      }
      return Promise.reject('People id and dismissal date is required');
    },
    onSuccess: (data) => {
      data.additions = makeAdditionOfMinimumLenth(data.additions);
      data.deductions.non_lop_deductions = makeNonLopDeductionsOfMinimumLength(
        data.deductions.non_lop_deductions,
        isHoldSalaryPayComplianceEnabled && isFnfProcessed
          ? DeductFrom.NET_PAY
          : DeductFrom.GROSS_PAY,
        isHoldSalaryPayComplianceEnabled && isFnfProcessed,
      );
      // Prefer the HRIS personal email when it already resolved.
      const hrisEmail =
        hrisFieldsQuery.data?.payrollEmployeeCustomFields?.fields?.['personal_email_address'];
      if (isHrisCustomFieldsEnabled && typeof hrisEmail === 'string' && hrisEmail !== '') {
        data.personal_email_address = hrisEmail;
      }
      setFnfData(data);
      if (data.net_pay_on_hold_from) {
        setSelectedHoldOption('1');
      }
      setExistingComponents({
        additions: data.additions,
        deductions: data.deductions.non_lop_deductions,
      });
    },
  });

  const finalizedPayrollMonthQuery = useQuery({
    enabled: !!peopleId,
    queryFn: () => api.runPayroll.getLastFinalizalizedPayrollMonth(peopleId as string),
  });

  const lastFinalizedPayrollMonth = finalizedPayrollMonthQuery.data?.last_finalized_payroll_month;

  const isFetchingDismissalDate = dismissalDateQuery.isLoading;
  const isErrorInFetchingDismissalDate = dismissalDateQuery.isError;

  const isFetchingFnfData = fnfDataQuery.isFetching;
  const isErrorInFetchingFnfData = fnfDataQuery.isError;

  const isFetchingLastPayrollMonth = finalizedPayrollMonthQuery.isFetching;
  const isErrorInFetchingLastFinalizedPayrollMonth = finalizedPayrollMonthQuery.isError;

  const isFetching =
    isFetchingDismissalDate ||
    isFetchingFnfData ||
    isFetchingLastPayrollMonth ||
    hrisFieldsQuery.isFetching;
  const isError =
    isErrorInFetchingDismissalDate ||
    isErrorInFetchingFnfData ||
    isErrorInFetchingLastFinalizedPayrollMonth ||
    hrisFieldsQuery.isError ||
    !peopleId;

  const processFnf = useMutation({
    mutationKey: UPDATE_FNF_DATA,
    mutationFn: ({
      fnfData,
      peopleId,
      dismissalDateInput,
    }: {
      fnfData: GetFnfResponseSchemaContract | null;
      peopleId: string | null;
      dismissalDateInput: Date | null;
    }) => {
      if (fnfData && peopleId && dismissalDateInput) {
        if (isM2S2Enabled) {
          return api.salaryComponents.mutations.updateFnfDetailsV2(
            fnfData,
            dismissalDateInput,
            peopleId,
            existingComponents,
            isM3S1Disabled,
          );
        } else if (isM2S1Enabled) {
          return api.salaryComponents.mutations.updateFnfDetails(
            fnfData,
            dismissalDateInput,
            peopleId,
            existingComponents,
            isM3S1Disabled,
          );
        } else {
          const startingMonth = selectedHoldOption === '1' ? fnfData.net_pay_on_hold_from : null;
          return api.fnf.updateFnfData(
            prepareFnfDataForUpdate(fnfData, dismissalDateInput, parseInt(peopleId), startingMonth),
          );
        }
      } else {
        return Promise.reject('Fnf data is required');
      }
    },
    onSuccess: () => {
      showToastViaEvent({
        text: 'Dismissal successfull!',
        timeout: 3000,
        type: 'success',
        icon: 'success',
      });
      window.location.href = `/employeeDetail?userId=${peopleId}`;
    },
    onError: (error: typeof AppError) => {
      const message = getAppErrorDetails(error);
      showErrorToastNotification({
        text: message.messageForUser,
        timeout: 3000,
      });
    },
  });
  return {
    isFetching,
    isError,
    processFnf,
    fnfData,
    setFnfData,
    lastFinalizedPayrollMonth,
    originalDismissalDate,
  };
};

export default useFnfApi;
